import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faTrophy, 
  faUsers, 
  faHandshake, 
  faLeaf,
  faPhone,
  faRocket,
  faBullseye,
  faGlobe
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import CallToAction from '../components/CallToAction';
import { numeriServizioClienti } from '../utils/data';

const ChiSiamo: React.FC = () => {
  return (
    <div>
      {/* Hero Section */}
      <section className="relative pt-32 pb-24 bg-gradient-hero text-white overflow-hidden">
        <div className="absolute inset-0 opacity-[0.07]">
          <div className="absolute top-20 right-20 w-[500px] h-[500px] bg-secondary rounded-full blur-[120px]" />
          <div className="absolute bottom-10 left-10 w-[400px] h-[400px] bg-accent rounded-full blur-[120px]" />
        </div>
        <div className="container mx-auto relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: -30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl mx-auto text-center"
          >
            <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-4">La nostra storia</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold mb-6 font-display">Chi Siamo</h1>
            <p className="text-xl text-white/75 leading-relaxed">
              Da oltre 10 anni offriamo un servizio gratuito di consulenza e comparazione 
              delle offerte per luce, gas e internet a famiglie e imprese.
            </p>
          </motion.div>
        </div>
      </section>

      {/* La Nostra Storia */}
      <section className="py-28">
        <div className="container mx-auto">
          <div className="flex flex-col lg:flex-row items-center gap-16">
            <motion.div 
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="lg:w-1/2"
            >
              <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-3">Il nostro percorso</span>
              <h2 className="text-3xl md:text-4xl font-extrabold mb-8 text-light-text font-display">La Nostra Storia</h2>
              <div className="space-y-5">
                <p className="text-light-muted leading-relaxed">
                  Rinova Group nasce con l'obiettivo di semplificare il mercato dell'energia,
                  offrendo un servizio di consulenza indipendente e gratuito che aiuta i consumatori 
                  a orientarsi tra le diverse offerte disponibili.
                </p>
                <p className="text-light-muted leading-relaxed">
                  Nel corso degli anni abbiamo ampliato il nostro servizio di comparazione, includendo 
                  anche soluzioni per la connettività internet, sempre con un approccio trasparente 
                  e rispettoso della libertà di scelta del cliente.
                </p>
                <p className="text-light-muted leading-relaxed">
                  Oggi migliaia di persone si affidano al nostro servizio per confrontare le offerte 
                  sul mercato. Il nostro ruolo è informare e consigliare: la decisione finale è 
                  sempre del cliente, senza alcun obbligo di sottoscrizione.
                </p>
              </div>
            </motion.div>
            <motion.div 
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:w-1/2"
            >
              <div className="bg-white p-8 md:p-10 rounded-3xl shadow-xl border border-light-border/30">
                <div className="grid grid-cols-2 gap-6">
                  {[
                    { value: '10+', label: 'Anni di esperienza', delay: 0 },
                    { value: '12k+', label: 'Consulenze erogate', delay: 0.1 },
                    { value: '40+', label: 'Fornitori confrontati', delay: 0.2 },
                    { value: '25+', label: 'Consulenti dedicati', delay: 0.3 },
                  ].map((stat) => (
                    <motion.div 
                      key={stat.label}
                      initial={{ opacity: 0, scale: 0.8 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.5, delay: stat.delay }}
                      className="text-center p-6 rounded-2xl bg-gradient-to-br from-primary/8 to-primary/3 border border-primary/10"
                    >
                      <div className="text-4xl font-extrabold text-primary mb-2 font-display">{stat.value}</div>
                      <p className="text-light-muted text-sm">{stat.label}</p>
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-28 bg-gradient-to-b from-white to-light-background">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-3">La nostra missione</span>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-light-text font-display">Cosa ci Guida</h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Trasparenza, indipendenza e rispetto per il cliente guidano ogni nostra azione
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { icon: faBullseye, title: 'Missione', desc: 'Offrire un servizio gratuito e indipendente di confronto offerte energetiche, garantendo trasparenza e libertà di scelta a ogni cliente.', color: 'primary' },
              { icon: faRocket, title: 'Visione', desc: 'Diventare il punto di riferimento per chi cerca un confronto imparziale delle offerte energetiche, rendendo il mercato più accessibile e comprensibile.', color: 'secondary' },
              { icon: faGlobe, title: 'Impegno', desc: 'Promuovere un approccio etico alla consulenza, dove il cliente è sempre informato, mai obbligato, e libero di prendere le proprie decisioni.', color: 'accent' },
            ].map((item, index) => (
              <motion.div 
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.15 * (index + 1) }}
                className="relative p-8 rounded-2xl bg-white border border-light-border/40 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all duration-300 overflow-hidden"
              >
                <div className={`absolute top-0 left-0 w-full h-1 bg-${item.color}`} />
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-${item.color}/10 text-${item.color} mb-6`}>
                  <FontAwesomeIcon icon={item.icon} className="text-xl" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-light-text font-display">{item.title}</h3>
                <p className="text-light-muted text-sm leading-relaxed">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* I Nostri Valori */}
      <section className="py-28">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-3">In cosa crediamo</span>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-light-text font-display">I Nostri Valori</h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Principi che guidano ogni nostra azione e decisione
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { icon: faTrophy, title: 'Indipendenza', desc: 'Confrontiamo le offerte in modo imparziale, senza favorire alcun fornitore specifico.', gradient: 'from-primary/12 to-primary/4', color: 'text-primary' },
              { icon: faUsers, title: 'Cliente al Centro', desc: 'Ascoltiamo le tue esigenze per offrirti un confronto su misura. La scelta è sempre tua.', gradient: 'from-secondary/12 to-secondary/4', color: 'text-secondary' },
              { icon: faHandshake, title: 'Trasparenza', desc: 'Nessun costo nascosto, nessun obbligo. Comunichiamo in modo chiaro e onesto.', gradient: 'from-accent/12 to-accent/4', color: 'text-accent' },
              { icon: faLeaf, title: 'Sostenibilità', desc: 'Includiamo nel confronto anche offerte green e soluzioni energetiche sostenibili.', gradient: 'from-primary/12 to-primary/4', color: 'text-primary' },
            ].map((valore, index) => (
              <motion.div 
                key={valore.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.1 * (index + 1) }}
                className="card text-center"
              >
                <div className={`inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-gradient-to-br ${valore.gradient} ${valore.color} mb-6`}>
                  <FontAwesomeIcon icon={valore.icon} className="text-2xl" />
                </div>
                <h3 className="text-lg font-bold mb-3 text-light-text font-display">{valore.title}</h3>
                <p className="text-light-muted text-sm leading-relaxed">{valore.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Numeri Servizio Clienti */}
      <section className="py-20 bg-gradient-to-b from-light-background to-white">
        <div className="container mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <span className="inline-block text-secondary font-semibold text-sm tracking-widest uppercase mb-3">Sempre disponibili</span>
            <h2 className="text-3xl md:text-4xl font-extrabold mb-4 text-light-text font-display">Servizio Clienti</h2>
            <p className="text-lg text-light-muted max-w-2xl mx-auto">
              Chiama gratuitamente per una consulenza senza impegno
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {numeriServizioClienti.map((item, index) => (
              <motion.a
                key={item.tel}
                href={`tel:${item.tel}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: 0.08 * (index + 1) }}
                whileHover={{ y: -4, scale: 1.02 }}
                className="flex items-center justify-center gap-3 p-5 rounded-2xl bg-white border border-light-border/40 shadow-card hover:shadow-card-hover hover:border-primary/30 transition-all duration-300 group"
              >
                <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary group-hover:text-white transition-all duration-300">
                  <FontAwesomeIcon icon={faPhone} className="text-primary text-sm group-hover:text-white" />
                </div>
                <span className="font-bold text-light-text font-display">{item.numero}</span>
              </motion.a>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <motion.section 
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.8 }}
        className="py-16 px-4"
      >
        <div className="container mx-auto">
          <CallToAction 
            title="Vuoi confrontare le offerte?" 
            subtitle="Parla con un nostro consulente per una consulenza gratuita e senza impegno"
            variant="accent"
          />
        </div>
      </motion.section>
    </div>
  );
};

export default ChiSiamo;
